import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../../Base/Context/AuthContext/AuthContext";

// --- COMPONENTES UI (PrimeReact & Custom) ---
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import Card from "../../../Base/Components/Card/Card";
import Loader from "../../../Base/Components/Loader/Loader";
import AgGrid from "../../../Base/Components/Tables/AgGrid/AgGrid";
import { ColoresEstados } from "../../../ColoresEstados/ColoresEstados";

// --- SERVICIOS Y API ---
import { getDatesHorarioNormal } from "../../../Base/Services/Moment/Luxon";
import { getListadoRequisaMateriales } from "../../../API/Mantenimiento/Requisas/RequisasMantenimientoRequests";

// --- COMPONENTES MODULARES ---
import { ModalRequisaMateriales } from "./Modal/ModalRequisaMateriales";
import ReporteRequisaMateriales from "./ReporteRequisaMateriales/ReporteRequisaMateriales";

/**
 * COMPONENTE PRINCIPAL: RequisaMaterialesScreen
 * DESCRIPCIÓN: Pantalla maestra para la gestión de materiales. Permite filtrar por fechas,
 * visualizar el estado de las requisas y acceder a la creación/edición de registros.
 */
const RequisaMaterialesScreen = () => {
  // --- ESTADOS (STATE) ---
  const [data, setData] = useState([]); // Almacena el listado de materiales de la API
  const [loading, setLoading] = useState(true); // Controla el estado de carga (Spinner)
  const [dates, setDates] = useState([new Date(), new Date()]); // Rango de fechas para el filtro
  const [IdRequisa, setIdRequisa] = useState(null); // ID seleccionado para pasar al Modal
  const [dialogRequisa, setDialogRequisa] = useState(false); // Control de visibilidad del Modal
  const [verReporte, setVerReporte] = useState(false);

  // --- CONTEXTO DE USUARIO ---
  const { userAuth } = useContext(AuthContext);

  /**
   * FUNCIÓN: ReqMateriales
   * Lógica para obtener el listado de materiales filtrado por rango de fechas y permisos de rol.
   */
  const getInfo = async () => {
    setLoading(true);
    // Normaliza el rango de fechas seleccionado para el backend
    const tempoDates = getDatesHorarioNormal(dates);
    // 1. DEFINICIÓN DEL DTO (Data Transfer Object) TEMPORAL
    // Se construye un objeto con la estructura que el SP (Stored Procedure) espera recibir.
    let temporal = {
      idUser: userAuth.idUser,
      IdRol: userAuth.IdRol,
      fecha1: tempoDates[0],
      fecha2: tempoDates[1],
    };

    const result = await getListadoRequisaMateriales(temporal);

    const ordenado = (result || []).sort((a, b) => b.IdRequisa - a.IdRequisa);

    setData(ordenado);
    setLoading(false);
  };

  /**
   * FUNCIÓN: OpenModal
   * Prepara el ID de la requisa y abre el diálogo de edición/creación.
   * @param {Object} rowData - Datos de la fila seleccionada en AgGrid.
   */
  const OpenModal = (rowData) => {
    setIdRequisa(rowData?.IdRequisa || null);
    setDialogRequisa(true);
  };

  /**
   * FUNCIÓN: hideDialog
   * Cierra el modal, limpia la referencia del ID y refresca la tabla.
   */
  const hideDialog = () => {
    setDialogRequisa(false);
    setIdRequisa(null);
    getInfo();
  };

  // --- CONFIGURACIÓN DE TABLA AG-GRID ---
  const table = {
    loading: loading,
    Data: data,
    onRowClicked: (row) => {
      setIdRequisa(row.data.IdRequisa);
    },
    Columns: [
      {
        field: "IdRequisa",
        header: "IdRequisa",
        headerEn: "",
        className: "Xsmall",
        hidden: userAuth.IdRol === 1,
      },
      {
        field: "Requisa",
        header: "Requisa",
        headerEn: "Requisition",
        className: "small",
        Sumary: "count",
        frozen: true,
        alignFrozen: "left",
        cellStyle: { fontWeight: "bold" },
        onCellDoubleClicked: ({ data }) => {
          if (data.IdRequisa) {
            setIdRequisa(data.IdRequisa);
            setDialogRequisa(true);
          }
        },
      },
      {
        field: "FechaRequisa",
        header: "Fecha",
        headerEn: "Date",
        className: "small",
        Format: `DateTime`,
        frozen: true,
        alignFrozen: "left",
      },
      // ... dentro de table.Columns
      {
        field: "StatusName",
        header: "Estado",
        headerEn: "Status",
        className: "medium",
        frozen: true,
        alignFrozen: "left",
        Format: "Template",
        editable: userAuth.IdRol === 1,
        // Prueba pasando el rowData directamente así
        body: (rowData) => <ColoresEstados rowData={rowData} tipo="estado" />,
      },
      {
        field: "NombreProducto",
        header: "Nombre Producto",
        headerEn: "Product New",
        className: "Xlarge",
      },
      {
        field: "Descripcion",
        header: "Descripcion",
        headerEn: "Description",
        className: "large",
      },
      {
        field: "Qty",
        header: "Cantidad",
        headerEn: "Quality",
        className: "Xsmall",
        Sumary: "sum",
        Format: "Currency",
        editable: userAuth.IdRol === 1,
      },
      {
        field: "UnitName",
        header: "Unidad",
        headerEn: "Quality",
        className: "Xsmall",
        editable: userAuth.IdRol === 1,
      },
      {
        field: "Comentarios",
        header: "Comentarios",
        headerEn: "Comments",
        className: "large",
      },
      {
        field: "Area",
        header: "Area",
        headerEn: "Area",
        className: "medium",
      },
      {
        field: "NombreEmpleado",
        header: "Asignado a",
        headerEn: "Assigned to",
        className: "large",
      },
      {
        field: "Solicita",
        header: "Solicita",
        headerEn: "Requestor",
        className: "large",
      },
      {
        field: "ComentariosRE",
        header: "Comentarios RE",
        headerEn: "RE Comments",
        className: "medium",
      },
      {
        field: "NombreTipo",
        header: "Nombre Tipo",
        headerEn: "Type Name",
        className: "medium",
      },
      {
        field: "NumeroIngreso",
        header: "Numero Ingreso",
        headerEn: "New Entry",
        className: "small",
      },
      {
        field: "CodigoProducto",
        header: "Codigo Producto",
        headerEn: "Product Code",
        className: "small",
      },
      {
        field: "Enviado",
        header: "Enviado",
        headerEn: "Sent",
        className: "small",
        Sumary: "sum",
        Format: "Decimal",
      },
      {
        field: "Balance",
        header: "Balance",
        headerEn: "Balasmallnce",
        className: "small",
        Sumary: "sum",
        Format: "Decimal",
      },
      {
        field: "Equipo",
        header: "Equipo",
        headerEn: "Equipment",
        className: "medium",
      },
      {
        field: "Tecnico",
        header: "Tecnico",
        headerEn: "Technician",
        className: "large",
      },
      {
        field: "CodigoTecnico",
        header: "Codigo Tecnico",
        headerEn: "Technician Code",
        className: "small",
      },
      {
        field: "FechaSalidaRequisa",
        header: "Fecha Salida",
        headerEn: "Departure Date",
        className: "large",
        Format: `DateTime`,
      },
      {
        field: "UsuarioDespacha",
        header: "Usuario Despacha",
        headerEn: "Dispatch User",
        className: "large",
      },
      {
        field: "AreaGeneral",
        header: "Area General",
        headerEn: "General Area",
        className: "medium",
      },
      {
        field: "UserName",
        header: "Usuario Creo",
        headerEn: "User Created",
        className: "large",
      },
      {
        field: "NombreUsuarioPermiso1",
        header: "Usuario Firmo",
        headerEn: "User Signature1",
        className: "large",
      },
      {
        field: "DatePermission1",
        header: "Fecha Firmo",
        headerEn: "Signature Date",
        className: "large",
        Format: `DateTime`,
      },
      {
        field: "NombreUsuarioPermiso3",
        header: "Usuario MAAS",
        headerEn: "User Signature3",
        className: "large",
      },
      {
        field: "DatePermission3",
        header: "Fecha MAAS",
        headerEn: "Signature Date 3",
        className: "large",
        Format: `DateTime`,
      },
      {
        field: "NombreUsuarioPermiso2",
        header: "Nombre Recibio",
        headerEn: "User Signature2",
        className: "large",
      },
      {
        field: "DatePermission2",
        header: "Fecha Recibido",
        headerEn: "Signature Date 2",
        className: "large",
        Format: `DateTime`,
      },
    ],
  };

  // Carga inicial al montar el componente
  useEffect(() => {
    getInfo();
  }, []);

  return (
    <div>
      {/* Pantalla de carga bloqueante para la sección de tabla */}
      {/* <Loader loading={loading} querySelector={'#ReqMateriales'} /> */}
      {/* <ConfirmDialog /> */}
      <Card
        titulo={
          <div className="d-flex gap">
            <h3>Requisas Materiales</h3>
            {/* Filtro de rango de fechas */}
            <Calendar
              value={dates}
              onChange={(e) => setDates(e.value)}
              selectionMode="range"
              showButtonBar
              showIcon
              dateFormat="dd/mm/yy"
            />
            {/* Botones de acción principal */}
            <Button icon="pi pi-search" onClick={getInfo} loading={loading} />
            <Button
              icon="pi pi-plus"
              onClick={() => OpenModal(null)}
              severity="success"
            />
            <Button
              type="button"
              icon="pi pi-print"
              className="p-button-secondary"
              disabled={!IdRequisa}
              onClick={() => setVerReporte(true)}
            />
          </div>
        }
        contenido={
          <div className="pt-4" style={{ height: "90vh" }} id="ReqMateriales">
            <AgGrid table={table} />
          </div>
        }
      />
      {/* Modal para creación y edición de detalles */}
      <ModalRequisaMateriales
        dialogRequisa={dialogRequisa}
        hideDialog={hideDialog}
        IdRequisa={IdRequisa}
      />
      {verReporte && (
        <ReporteRequisaMateriales
          showReporte={verReporte}
          setShowReporte={setVerReporte}
          IdRequisa={IdRequisa}
        />
      )}
    </div>
  );
};

export default RequisaMaterialesScreen;
